import { useQuery } from '@tanstack/react-query';
import { motion } from 'framer-motion';
import { Link } from '@tanstack/react-router';
import {
  ClipboardList, Wrench, Gauge, Boxes, ArrowUpRight, Activity, Building2, CheckCircle2,
} from 'lucide-react';
import { PageHeader } from '@/components/PageHeader';
import { Card, CardDescription, CardTitle } from '@/components/Card';
import { StatusPill } from '@/components/StatusPill';
import { Skeleton } from '@/components/EmptyState';
import { api, getActiveCompany } from '@/lib/api';
import { me } from '@/lib/auth';

interface ListWrap<T> { items: T[] }
interface Asset            { id: string; status: string }
interface MaintenanceReq   { id: string; status: string }
interface WorkOrder        { id: string; status: string; total_cost: string }
interface Allocation       { id: string; status: string }

const ASSET_STATUS_TONE: Record<string, 'success' | 'info' | 'warning' | 'neutral' | 'danger' | 'accent'> = {
  'Submitted': 'success',
  'Partially Depreciated': 'info',
  'Fully Depreciated': 'neutral',
  'Draft': 'neutral',
  'Sold': 'warning',
  'Scrapped': 'danger',
  'Available': 'success',
  'In Use': 'info',
  'Under Maintenance': 'warning',
  'Idle': 'neutral',
};

export function OperationsOverview() {
  const activeCompany = getActiveCompany();

  const meQ        = useQuery({ queryKey: ['me'], queryFn: () => me() });
  const assetsQ    = useQuery({ queryKey: ['ops', 'assets'],              queryFn: () => api<ListWrap<Asset>>('/assets/assets') });
  const maintQ     = useQuery({ queryKey: ['ops', 'maintenance'],         queryFn: () => api<ListWrap<MaintenanceReq>>('/maintenance/maintenance-requests') });
  const workOrderQ = useQuery({ queryKey: ['ops', 'asset-work-orders'],   queryFn: () => api<ListWrap<WorkOrder>>('/maintenance/asset-work-orders') });
  const allocQ     = useQuery({ queryKey: ['ops', 'asset-allocations'],   queryFn: () => api<ListWrap<Allocation>>('/assets/asset-allocations') });
  const companiesQ = useQuery({ queryKey: ['ops', 'companies'],           queryFn: () => api<ListWrap<{ id: string; name: string }>>('/accounting/companies') });

  const assets       = assetsQ.data?.items ?? [];
  const maintReqs    = maintQ.data?.items ?? [];
  const workOrders   = workOrderQ.data?.items ?? [];
  const allocations  = allocQ.data?.items ?? [];

  const openMaint     = maintReqs.filter((m) => m.status === 'open' || m.status === 'in_progress').length;
  const openWorkOrders = workOrders.filter((w) => w.status === 'draft' || w.status === 'in_progress').length;
  const activeAllocs  = allocations.filter((a) => a.status === 'active').length;

  // Assets grouped by their depreciation lifecycle status (Draft / Submitted /
  // Partially Depreciated / Fully Depreciated / Sold / Scrapped).
  const assetsByStatus = assets.reduce<Record<string, number>>((acc, a) => {
    const k = a.status || 'Unknown';
    acc[k] = (acc[k] ?? 0) + 1;
    return acc;
  }, {});
  const assetStatusRows = Object.entries(assetsByStatus).sort((a, b) => b[1] - a[1]);
  // Fleet KPIs from real operating signals (the asset `status` field is a
  // depreciation lifecycle, not an availability state, so we don't use it here):
  //   a unit with an open/in-progress maintenance request counts as "down";
  //   an active allocation counts as "deployed" to a contract.
  //   PA (Physical Availability) = available / fleet
  //   UA (Utilisation)           = deployed / available
  const totalUnits    = assets.length;
  const downUnits     = Math.min(openMaint, totalUnits);
  const availUnits    = totalUnits - downUnits;
  const deployedUnits = Math.min(activeAllocs, availUnits);
  const paPct = totalUnits ? Math.round((availUnits / totalUnits) * 100) : 0;
  const uaPct = availUnits ? Math.round((deployedUnits / availUnits) * 100) : 0;

  const companies = meQ.data?.companies ?? [];
  const companyName = (id: string) => companiesQ.data?.items?.find((c) => c.id === id)?.name ?? id;

  const tiles = [
    { label: 'Total assets',          value: assets.length,    icon: ClipboardList, link: '/assets/assets',                       loading: assetsQ.isLoading },
    { label: 'Fleet availability (PA)',  value: `${paPct}%`,   icon: Gauge,         link: '/assets/assets',                       loading: assetsQ.isLoading },
    { label: 'Fleet utilisation (UA)',   value: `${uaPct}%`,   icon: Gauge,         link: '/assets/assets',                       loading: assetsQ.isLoading },
    { label: 'Open maintenance',      value: openMaint,        icon: Wrench,        link: '/maintenance/maintenance-requests',    loading: maintQ.isLoading },
    { label: 'Open work orders',      value: openWorkOrders,   icon: Gauge,         link: '/maintenance/asset-work-orders',       loading: workOrderQ.isLoading },
    { label: 'Active allocations',    value: activeAllocs,     icon: Boxes,         link: '/assets/asset-allocations',            loading: allocQ.isLoading },
  ];

  return (
    <>
      <PageHeader
        crumbs={[{ label: 'Operations Overview' }]}
        title="Operations Overview"
        subtitle={`Asset operations snapshot for the active company${activeCompany ? ` — ${companyName(activeCompany)}` : ''}.`}
      />

      <div className="flex-1 px-4 sm:px-6 lg:px-8 pt-6 pb-8 space-y-6">
        {/* KPI tiles */}
        <motion.div
          initial="hidden" animate="visible"
          variants={{ visible: { transition: { staggerChildren: 0.04 } } }}
          className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4"
        >
          {tiles.map((t) => (
            <motion.div key={t.label} variants={{ hidden: { opacity: 1, y: 0 }, visible: { opacity: 1, y: 0 } }} className="min-w-0">
              <Link to={t.link as never} className="block group">
                <Card className="hover:border-stone/40 transition-colors cursor-pointer overflow-hidden">
                  <div className="flex items-start justify-between mb-3">
                    <div className="inline-flex size-9 items-center justify-center rounded-lg bg-surface text-ink group-hover:text-brand-green-deep transition-colors">
                      <t.icon className="size-4" />
                    </div>
                    <ArrowUpRight className="size-3.5 text-stone group-hover:text-ink transition-colors shrink-0" />
                  </div>
                  <div className="text-caption text-stone">{t.label}</div>
                  <div className="mt-1 text-heading-4 lg:text-heading-5 xl:text-heading-4 2xl:text-heading-3 leading-tight num text-ink break-words">
                    {t.loading ? <Skeleton className="h-7 w-20 mt-1" /> : t.value}
                  </div>
                </Card>
              </Link>
            </motion.div>
          ))}
        </motion.div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
          {/* Assets by depreciation lifecycle status */}
          <Card className="lg:col-span-2">
            <div className="flex items-start justify-between mb-4">
              <div>
                <CardTitle>Assets by depreciation status</CardTitle>
                <CardDescription>Depreciation lifecycle across the fixed-asset register.</CardDescription>
              </div>
              <Activity className="size-4 text-stone" />
            </div>
            {assetsQ.isLoading ? (
              <Skeleton className="h-32 w-full" />
            ) : assetStatusRows.length === 0 ? (
              <div className="text-center py-8 text-stone text-body-sm">No assets yet.</div>
            ) : (
              <ul className="divide-y divide-hairline -mx-5">
                {assetStatusRows.map(([status, count]) => {
                  const pct = assets.length > 0 ? Math.round((count / assets.length) * 100) : 0;
                  return (
                    <li key={status} className="px-5 py-2.5 flex items-center gap-3">
                      <StatusPill tone={ASSET_STATUS_TONE[status] ?? 'neutral'} withDot={false}>{status}</StatusPill>
                      <div className="flex-1 h-1.5 rounded-full bg-surface overflow-hidden">
                        <div className="h-full bg-brand-green rounded-full" style={{ width: `${pct}%` }} />
                      </div>
                      <div className="text-body-sm num text-ink w-16 text-right">{count} <span className="text-stone text-caption">({pct}%)</span></div>
                    </li>
                  );
                })}
              </ul>
            )}
          </Card>

          {/* Maintenance pipeline */}
          <Card>
            <div className="flex items-start justify-between mb-4">
              <div>
                <CardTitle>Maintenance pipeline</CardTitle>
                <CardDescription>Open requests and active work orders.</CardDescription>
              </div>
              <Wrench className="size-4 text-stone" />
            </div>
            <div className="space-y-3 text-body-sm">
              <PipelineRow label="Open / in-progress requests" value={openMaint} total={maintReqs.length} loading={maintQ.isLoading} />
              <PipelineRow label="Open / in-progress work orders" value={openWorkOrders} total={workOrders.length} loading={workOrderQ.isLoading} />
              <PipelineRow label="Completed work orders" value={workOrders.filter((w) => w.status === 'completed').length} total={workOrders.length} loading={workOrderQ.isLoading} icon={CheckCircle2} />
            </div>
          </Card>
        </div>

        {/* Per-company breakdown — me().companies are IDs only; we aggregate for
            the active company and list the rest so multi-entity context is visible. */}
        <Card>
          <div className="flex items-start justify-between mb-4">
            <div>
              <CardTitle>Companies</CardTitle>
              <CardDescription>
                Figures above are scoped to the active company. Switch the workspace to view another entity.
              </CardDescription>
            </div>
            <Building2 className="size-4 text-stone" />
          </div>
          {meQ.isLoading ? (
            <Skeleton className="h-16 w-full" />
          ) : companies.length === 0 ? (
            <div className="text-center py-6 text-stone text-body-sm">No companies on your account.</div>
          ) : (
            <ul className="flex flex-wrap gap-2">
              {companies.map((c) => {
                const active = c === activeCompany;
                return (
                  <li key={c}>
                    <span className={'inline-flex items-center gap-2 rounded-md border px-3 py-1.5 text-body-sm ' +
                      (active ? 'border-brand-green/40 bg-brand-green/5 text-ink' : 'border-hairline text-text-secondary')}>
                      <Building2 className="size-3.5" />
                      <span className="text-caption">{companyName(c)}</span>
                      {active && <span className="text-caption text-brand-green">● Active</span>}
                    </span>
                  </li>
                );
              })}
            </ul>
          )}
        </Card>
      </div>
    </>
  );
}

function PipelineRow({ label, value, total, loading, icon: Icon }: {
  label: string; value: number; total: number; loading?: boolean; icon?: React.ComponentType<{ className?: string }>;
}) {
  return (
    <div className="flex items-center justify-between gap-3">
      <span className="text-stone inline-flex items-center gap-1.5">{Icon ? <Icon className="size-3.5" /> : null}{label}</span>
      <span className="num text-ink">
        {loading ? <Skeleton className="h-4 w-10 inline-block" /> : <>{value}<span className="text-stone text-caption"> / {total}</span></>}
      </span>
    </div>
  );
}
